import React, { useEffect, useState } from 'react';

import {
	Checkbox,
	DatepickerRange,
	RangeSlider,
	SubmissionButtons,
} from '@components/common';

import { useQueryAssetFilterThresholds } from '@hooks/assets/useQueryAssetFilterThresholds';

import { map } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import {
	applyFilterValues,
	resetDetailedFilters,
	resetRightSideControls,
	setDetailedFilters,
	setWithoutHeightParam,
} from '@slices/map';

import { TRangeSliderValue, TSliderValue } from '@components/common/RangeSlider/RangeSlider.types';

import tabStyles from './Tabs.module.scss';
import commonStyles from '@components/Map/MapControls/Controls/Filters/AssetsFilters/AssetsFilters.module.scss';

const fractionDigits = 2;

const Detailed = () => {
	const {
		currentAssetsFilterValues: { detailed },
		appliedAssetsFilterValues: { detailed: detailedApplied },
		pinnedFilter,
	} = useAppSelector(map);
	const dispatch = useAppDispatch();

	const { thresholds } = useQueryAssetFilterThresholds();
	const { height, pricing } = thresholds || {};

	const {
		fromDate,
		toDate,
	} = detailed?.availabilityReviewDate || {};

	const { height: _height, indicativeAcceptablePricing: _price } = detailedApplied || {};
	const { from: _fromDate, to: _toDate } = detailedApplied?.siteAvailabilityReviewDate || {};

	const heightFrom = _height?.from ? _height.from : height.from;
	const heightTo = _height?.to ? _height.to : height.to;
	const priceFrom = _price?.from ? _price.from : pricing.from;
	const priceTo = _price?.to ? _price.to : pricing.to;

	const [heightValue, setHeightValue] = useState<TSliderValue<string[]>>([
		heightFrom.toString(),
		heightTo.toString(),
	]);
	const [priceValue, setPriceValue] = useState<TSliderValue<string[]>>([
		priceFrom.toString(),
		priceTo.toString(),
	]);

	const [dateRange, setDateRange] = useState({
		from: _fromDate || fromDate || null,
		to: _toDate || toDate || null,
	});

	useEffect(() => {
		setDateRange({
			from: dateRange.from || _fromDate || fromDate || null,
			to: dateRange.to || _toDate || toDate || null,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fromDate, _fromDate, toDate, _toDate]);

	const [isDisableApplyButton, setIsDisableApplyButton] = useState(false);

	const handleChangeFromDate = (date: number) => {
		setDateRange(prevState => ({
			...prevState,
			from: date,
		}));

		dispatch(setDetailedFilters({
			...detailed,
			availabilityReviewDate: {
				...detailed?.availabilityReviewDate,
				fromDate: date,
			},
		}));

		setIsDisableApplyButton(false);
	};

	const handleChangeToDate = (date: number) => {
		setDateRange(prevState => ({
			...prevState,
			to: date,
		}));

		dispatch(setDetailedFilters({
			...detailed,
			availabilityReviewDate: {
				...detailed?.availabilityReviewDate,
				toDate: date,
			},
		}));

		setIsDisableApplyButton(false);
	};

	const handleChangeHeight = (value: TRangeSliderValue) => {
		if (Array.isArray(value)) {
			dispatch(setDetailedFilters({
				...detailed,
				height: {
					from: value[0],
					to: value[1],
				},
			}));
		}

		setIsDisableApplyButton(false);
	};

	const handleChangePricing = (value: TRangeSliderValue) => {
		if (Array.isArray(value)) {
			dispatch(setDetailedFilters({
				...detailed,
				acceptablePrice: {
					from: value[0],
					to: value[1],
				},
			}));
		}

		setIsDisableApplyButton(false);
	};

	const handleChangeHeightParam = () => {
		dispatch(setWithoutHeightParam(!detailed?.withoutHeight));

		setIsDisableApplyButton(false);
	};

	const resetFilter = () => {
		setHeightValue([height?.from.toString(), height?.to.toString()]);
		setPriceValue([pricing?.from.toString(), pricing?.to.toString()]);
		setDateRange({
			from: null,
			to: null,
		});

		dispatch(setWithoutHeightParam(false));
		dispatch(resetDetailedFilters());
		dispatch(applyFilterValues({}));

		if (!pinnedFilter) dispatch(resetRightSideControls());
	};

	const applyFilters = () => {
		dispatch(applyFilterValues({}));

		if (!pinnedFilter) dispatch(resetRightSideControls());
	};

	useEffect(() => {
		if (detailedApplied) setIsDisableApplyButton(true);
	}, [detailedApplied]);

	const minHeightValue = Math.floor(Number(height?.from?.toFixed(fractionDigits)));
	const maxHeightValue = Math.floor(Number(height?.to?.toFixed(fractionDigits)));

	const maxPriceValue = Math.floor(Number(pricing?.to?.toFixed(fractionDigits)));
	const minPriceValue = Math.floor(Number(pricing?.from?.toFixed(fractionDigits)));

	return height && pricing ? (
			<div className={tabStyles.detailedContainer}>
				<div className={commonStyles.heightParamContainer}>
					<div className={commonStyles.title}>Height (m)</div>
					<Checkbox
						checked={detailed?.withoutHeight}
						label='Show assets without height'
						name='withoutHeight'
						onChange={handleChangeHeightParam}
					/>
					<RangeSlider
						disabled={detailed?.withoutHeight}
						fromName='heightFrom'
						fromPlaceholder='Height from'
						handleChange={handleChangeHeight}
						max={maxHeightValue}
						min={minHeightValue}
						setValue={setHeightValue}
						toName='heightTo'
						toPlaceholder='Height to'
						value={heightValue}
						valueLabelPrecision={0}
					/>
				</div>
				<RangeSlider
					fractionDigits={fractionDigits}
					fromName='priceFrom'
					fromPlaceholder='Price from'
					handleChange={handleChangePricing}
					label='Indicate Acceptable Pricing (£)'
					max={maxPriceValue}
					min={minPriceValue}
					setValue={setPriceValue}
					toName='priceTo'
					toPlaceholder='Price to'
					value={priceValue}
					valueLabelPrecision={0}
				/>
				<div className={commonStyles.datePickersContainer}>
					<DatepickerRange
						disableInput
						groupLabel='Site Availability Review Date'
						handleChangeFromDate={handleChangeFromDate}
						handleChangeToDate={handleChangeToDate}
						isMobileDevice={false}
						value={{
							from: dateRange.from,
							to: dateRange.to,
						}}
					/>
				</div>
				<SubmissionButtons
					className={commonStyles.buttonsContainer}
					disabledPositive={isDisableApplyButton}
					negativeHandler={resetFilter}
					negativeLabel='Reset all'
					positiveHandler={applyFilters}
					positiveLabel='Apply'
				/>
			</div>
		)
		: null;
};

export default Detailed;
